
import { useCallback, useState, useEffect } from 'react';
import { AnimatePresence, type AnimationDefinition, motion } from 'framer-motion';
import { useDataChannel } from '@livekit/components-react';
import type { AppConfig } from '@/app-config';
import { SessionView } from '@/components/app/session-view';
import { WelcomeView } from '@/components/app/welcome-view';
import { useConnection } from '@/hooks/useConnection';
// import { useLangfuseConfig } from '@/hooks/useLangfuseConfig'; // Not needed - using interview mode
import { FeedbackDialog } from '@/components/feedback/FeedbackDialog';

const MotionWelcomeView = motion.create(WelcomeView);
const MotionSessionView = motion.create(SessionView);

const VIEW_MOTION_PROPS = {
  variants: {
    visible: {
      opacity: 1,
    },
    hidden: {
      opacity: 0,
    },
  },
  initial: 'hidden',
  animate: 'visible',
  exit: 'hidden',
  transition: {
    duration: 0.5,
    ease: 'linear',
  },
};

interface ViewControllerProps {
  appConfig: AppConfig;
}

export function ViewController({ appConfig }: ViewControllerProps) {
  const { isConnectionActive, connect, onDisconnectTransitionComplete } = useConnection();
  // const { loading: langfuseLoading } = useLangfuseConfig(); // Not needed - no loading gate

  // Feedback dialog state
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);

  // Listen for session_id from agent via data channel (like NihongoSensei)
  const { message } = useDataChannel('langfuse_session');

  useEffect(() => {
    if (message) {
      try {
        const decoder = new TextDecoder();
        const text = decoder.decode(message.payload);
        const data = JSON.parse(text);
        if (data.session_id) {
          setSessionId(data.session_id);
        }
      } catch (e) {
        console.error('Failed to parse session data:', e);
      }
    }
  }, [message]);

  const handleAnimationComplete = useCallback(
    (definition: AnimationDefinition) => {
      // manually end the session when the exit animation completes
      if (definition === 'hidden') {
        onDisconnectTransitionComplete();
        // Show feedback dialog if we have a session_id
        if (sessionId) {
          setShowFeedbackDialog(true);
        }
      }
    },
    [onDisconnectTransitionComplete, sessionId]
  );

  const handleFeedbackSubmit = async (feedback: { score?: number; text?: string }) => {
    if (!sessionId) {
      return;
    }

    try {
      const response = await fetch('/api/submit-feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          session_id: sessionId,
          rating: feedback.score,
          feedback_text: feedback.text,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to submit feedback');
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      throw error;
    }
  };

  const handleFeedbackClose = () => {
    setShowFeedbackDialog(false);
    setSessionId(null);
  };

  return (
    <>
      <AnimatePresence mode="wait">
        {/* Welcome view */}
        {!isConnectionActive && (
          <MotionWelcomeView
            key="welcome"
            {...VIEW_MOTION_PROPS}
            onStartCall={connect}
            isLoading={false} // No loading gate needed in interview mode
          />
        )}
        {/* Session view */}
        {isConnectionActive && (
          <MotionSessionView
            key="session-view"
            {...VIEW_MOTION_PROPS}
            appConfig={appConfig}
            onAnimationComplete={handleAnimationComplete}
          />
        )}
      </AnimatePresence>

      {/* Feedback Dialog - Always rendered at root level (like NihongoSensei) */}
      {sessionId && (
        <FeedbackDialog
          isOpen={showFeedbackDialog}
          onClose={handleFeedbackClose}
          sessionId={sessionId}
          onSubmit={handleFeedbackSubmit}
        />
      )}
    </>
  );
}

